/**
 * Detalle de profesional: info + edición + cuenta de acceso.
 */
import { useEffect, useState, type FormEvent } from "react";
import { TelefonoInput, formatearTelefono } from "../components/TelefonoInput";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CalendarClock,
  CalendarOff,
  Check,
  Copy,
  Edit2,
  FileSignature,
  KeyRound,
  Plus,
  RefreshCw,
  Stethoscope,
  Trash2,
  UserPlus,
} from "lucide-react";
import {
  type AgendaBloqueoCreate,
  type AgendaBloqueo,
  type AgendaRegla,
  DIAS_SEMANA,
  createBloqueo,
  createRegla,
  deleteBloqueo,
  deleteRegla,
  listBloqueos,
  listReglas,
  updateRegla,
} from "../api/agenda";
import {
  type CrearCuentaProfesional,
  type EspecialidadMini,
  type Profesional,
  type ProfesionalUpdate,
  type UsuarioMini,
  crearCuentaProfesional,
  getProfesional,
  listEspecialidades,
  listMyTenantRoles,
  updateProfesional,
} from "../api/profesionales";
import { listSedes } from "../api/tenants";
import { useAuth } from "../auth/AuthProvider";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import {
  CalendarioDisponibilidad,
  proximoDiaDisponible,
} from "../components/CalendarioDisponibilidad";
import { hoyEnZona } from "../lib/fecha";
import { ApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../components/ui";
import { fechaHora } from "../lib/fecha";

export function ProfesionalDetailPage() {
  const { id } = useParams<{ slug: string; id: string }>();
  const base = useBase();
  const { me } = useAuth();
  const profId = id ? Number(id) : NaN;
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);

  // Editar la ficha, los horarios y dar de baja son `profesional:update`, que
  // solo tiene admin_tenant. Médico y recepción entran a mirar: si les
  // mostramos los botones, solo cosechan 403.
  const puedeEditar = me?.is_super_admin || (me?.permisos.includes("profesional:update") ?? false);

  const { data: prof, isLoading, error } = useQuery({
    queryKey: ["profesional", profId],
    queryFn: () => getProfesional(profId),
    enabled: Number.isFinite(profId),
  });

  // Dar de baja se confirma; reactivar no: lo primero apaga una agenda entera,
  // lo segundo solo devuelve las cosas a como estaban.
  const [porDarDeBaja, setPorDarDeBaja] = useState(false);
  const toggleMutation = useMutation({
    mutationFn: (payload: ProfesionalUpdate) => updateProfesional(profId, payload),
    onSuccess: () => {
      setPorDarDeBaja(false);
      queryClient.invalidateQueries({ queryKey: ["profesional", profId] });
      queryClient.invalidateQueries({ queryKey: ["profesionales"] });
    },
  });

  if (!Number.isFinite(profId)) return <Navigate to={`${base}/profesionales`} replace />;

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/profesionales`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Todos los profesionales
      </Link>

      {isLoading && <div className="text-sm text-slate-400">Cargando…</div>}
      {error && <Alert>No se pudo cargar el profesional.</Alert>}

      {prof && (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)] flex items-center justify-center">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{prof.nombre_completo}</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  <span className="font-mono">
                    {prof.tipo_colegio} {prof.colegiatura}
                  </span>
                  {prof.rne && (
                    <>
                      {" "}· RNE <span className="font-mono">{prof.rne}</span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={prof.activo ? "success" : "danger"}>
                {prof.activo ? "Activo" : "Inactivo"}
              </Badge>
              {/* Llega a la Agenda ya filtrada por este profesional, en vez de
                  obligar a re-seleccionarlo por especialidad teniendo la ficha
                  abierta. La especialidad va en la URL porque el embudo de
                  Agenda la necesita para poblar el selector de profesional. */}
              <Link
                to={`${base}/agenda?profesional=${prof.id}${
                  prof.especialidades[0] ? `&especialidad=${prof.especialidades[0].id}` : ""
                }`}
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <CalendarClock className="w-4 h-4" />
                Ver agenda
              </Link>
              {puedeEditar && (
                <Button variant="ghost" onClick={() => setEditOpen(true)}>
                  <Edit2 className="w-4 h-4" />
                  Editar
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Datos personales</h2>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <Row label="Documento" value={`${prof.tipo_documento} ${prof.num_documento}`} />
                <Row
                  label="Sexo"
                  value={prof.sexo === "M" ? "Masculino" : prof.sexo === "F" ? "Femenino" : "—"}
                />
                <Row label="Teléfono" value={formatearTelefono(prof.telefono)} />
                <Row label="Email" value={prof.email ?? "—"} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Especialidades</h2>
              </CardHeader>
              <CardBody>
                {prof.especialidades.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin especialidades asignadas.</p>
                ) : (
                  <div className="space-y-1.5">
                    {prof.especialidades.map((e) => (
                      <div key={e.id} className="flex items-center gap-2">
                        <Badge>{e.nombre}</Badge>
                        {e.principal && (
                          <span className="text-xs text-[color:var(--color-link)] font-medium">principal</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <h2 className="font-semibold text-slate-900">Sedes donde atiende</h2>
            </CardHeader>
            <CardBody>
              {prof.sedes.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Sin sedes asignadas — el profesional no se mostrará como disponible en ninguna sede.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {prof.sedes.map((s) => (
                    <div key={s.id} className="flex items-center gap-1.5">
                      <Badge>{s.nombre}</Badge>
                      {s.principal && (
                        <span className="text-xs text-[color:var(--color-link)] font-medium">principal</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <AccountAccessCard
            prof={prof}
            onOpenCreate={() => setCreateAccountOpen(true)}
          />

          <Card>
            <CardHeader>
              <h2 className="font-semibold text-slate-900">Firma digital</h2>
            </CardHeader>
            <CardBody>
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <FileSignature className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {prof.firma_digital_activa ? "Firma digital configurada" : "Sin firma digital"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Requerida para emitir recetas de psicotrópicos y firmar HCE de forma reconocida.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    toggleMutation.mutate({
                      firma_digital_activa: !prof.firma_digital_activa,
                    })
                  }
                  disabled={toggleMutation.isPending}
                  className="text-xs text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium disabled:opacity-50"
                >
                  {prof.firma_digital_activa ? "Desactivar" : "Activar"}
                </button>
              </div>
            </CardBody>
          </Card>

          <AgendaProfesionalCard prof={prof} puedeEditar={puedeEditar} />

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-700">
                  {prof.activo
                    ? "Este profesional puede atender pacientes y firmar HCE."
                    : "Profesional dado de baja — no puede registrar nuevas atenciones."}
                </p>
                {puedeEditar && (
                  <button
                    onClick={() =>
                      prof.activo
                        ? setPorDarDeBaja(true)
                        : toggleMutation.mutate({ activo: true })
                    }
                    disabled={toggleMutation.isPending}
                    className={`text-xs font-medium ${
                      prof.activo
                        ? "text-red-600 hover:text-red-800"
                        : "text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
                    } disabled:opacity-50`}
                  >
                    {prof.activo ? "Dar de baja" : "Reactivar"}
                  </button>
                )}
              </div>
            </CardBody>
          </Card>

          <EditProfesionalDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            prof={prof}
          />
          <CreateAccountDialog
            open={createAccountOpen}
            onClose={() => setCreateAccountOpen(false)}
            prof={prof}
          />

          <ConfirmDialog
            open={porDarDeBaja}
            onClose={() => setPorDarDeBaja(false)}
            onConfirm={() => toggleMutation.mutate({ activo: false })}
            loading={toggleMutation.isPending}
            title="Dar de baja al profesional"
            description="Deja de poder registrar atenciones y no se le podrán agendar citas nuevas. Sus citas ya agendadas y su historial no se tocan; se puede reactivar cuando haga falta."
            detalle={<span className="font-medium">{prof.nombre_completo}</span>}
            confirmLabel="Sí, darlo de baja"
            cancelLabel="No, dejarlo activo"
          />
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 last:border-0 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium">{value}</span>
    </div>
  );
}

// ---------- Account access card ----------

function AccountAccessCard({
  prof,
  onOpenCreate,
}: {
  prof: Profesional;
  onOpenCreate: () => void;
}) {
  if (prof.usuario) {
    return <AccountAccessExisting usuario={prof.usuario} />;
  }
  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Cuenta de acceso</h2>
      </CardHeader>
      <CardBody>
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <KeyRound className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-900">
                Sin cuenta de acceso al sistema
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Este profesional está registrado pero no puede iniciar sesión.
                Para que pueda firmar HCE o emitir recetas, crea una cuenta de acceso.
              </p>
            </div>
          </div>
          <Button onClick={onOpenCreate}>
            <UserPlus className="w-4 h-4" />
            Crear cuenta
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function AccountAccessExisting({ usuario }: { usuario: UsuarioMini }) {
  const tz = useZonaHoraria();
  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Cuenta de acceso</h2>
      </CardHeader>
      <CardBody>
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-[color:var(--color-link)] mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{usuario.email}</p>
            <p className="text-xs text-slate-500 mt-1">
              Estado:{" "}
              <span className={usuario.activo ? "text-[color:var(--color-link)]" : "text-red-700"}>
                {usuario.activo ? "Activa" : "Inactiva"}
              </span>{" "}
              · Último ingreso:{" "}
              {usuario.ultimo_login
                ? fechaHora(usuario.ultimo_login, tz)
                : "nunca"}
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Para cambiar contraseña, asignar más roles o desactivar la cuenta, gestiónalo
              desde la sección Usuarios del policlínico.
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------- Create account dialog ----------

function generatePassword(): string {
  const letters = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const pick = (s: string, n: number) =>
    Array.from({ length: n }, () => s[Math.floor(Math.random() * s.length)]).join("");
  return pick(letters, 4) + pick(digits, 4) + pick(letters, 2);
}

function CreateAccountDialog({
  open,
  onClose,
  prof,
}: {
  open: boolean;
  onClose: () => void;
  prof: Profesional;
}) {
  const queryClient = useQueryClient();

  const { data: rolesRaw = [] } = useQuery({
    queryKey: ["my-tenant-roles"],
    queryFn: listMyTenantRoles,
    enabled: open,
    staleTime: 60_000,
  });
  // Defensa contra duplicados/globales: deduplica por codigo y excluye super_admin.
  const roles = (() => {
    const seen = new Set<string>();
    const out: typeof rolesRaw = [];
    for (const r of rolesRaw) {
      if (r.codigo === "super_admin") continue;
      if (seen.has(r.codigo)) continue;
      seen.add(r.codigo);
      out.push(r);
    }
    return out;
  })();

  // Pre-fill email con el del profesional si tiene; pre-marcar rol "medico" por default
  const [email, setEmail] = useState(prof.email ?? "");
  const [password, setPassword] = useState(generatePassword());
  const [rolIds, setRolIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createdCreds, setCreatedCreds] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  // Cuando se cargan los roles, marcar "medico" por default si está disponible
  useEffect(() => {
    if (open && roles.length > 0 && rolIds.length === 0) {
      const medico = roles.find((r) => r.codigo === "medico");
      if (medico) setRolIds([medico.id]);
    }
  }, [open, roles, rolIds.length]);

  const mutation = useMutation({
    mutationFn: (payload: CrearCuentaProfesional) =>
      crearCuentaProfesional(prof.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profesional", prof.id] });
      queryClient.invalidateQueries({ queryKey: ["profesionales"] });
      setCreatedCreds({ email, password });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "No se pudo crear la cuenta.");
      } else {
        setError("Error de conexión.");
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ email, password, rol_ids: rolIds });
  }

  async function copyCreds() {
    if (!createdCreds) return;
    try {
      await navigator.clipboard.writeText(
        `Email: ${createdCreds.email}\nContraseña: ${createdCreds.password}`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  function handleClose() {
    setCreatedCreds(null);
    setError(null);
    setCopied(false);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={createdCreds ? "Cuenta creada" : "Crear cuenta de acceso"}
      description={
        createdCreds
          ? "Comparte estas credenciales con el profesional."
          : `Vincula una cuenta de Usuario a ${prof.nombre_completo}.`
      }
    >
      {createdCreds ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)] p-4">
            <p className="text-sm font-semibold text-[color:var(--color-link)]">
              ✓ Cuenta de acceso creada
            </p>
            <p className="text-xs text-[color:var(--color-link)] mt-1">
              Esta contraseña no se volverá a mostrar.
            </p>
            <div className="mt-3 font-mono text-xs space-y-1 bg-white border border-[color:var(--color-link)] rounded p-2">
              <div>
                <span className="text-slate-500">Email: </span>
                <span className="text-slate-900">{createdCreds.email}</span>
              </div>
              <div>
                <span className="text-slate-500">Contraseña: </span>
                <span className="text-slate-900">{createdCreds.password}</span>
              </div>
            </div>
            <button
              onClick={copyCreds}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copiado" : "Copiar email + contraseña"}
            </button>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert>{error}</Alert>}

          <Field label="Email" htmlFor="ca_email" hint="Identificador para iniciar sesión.">
            <Input
              id="ca_email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dr.perez@policlinico-san-juan.pe"
              required
            />
          </Field>

          <Field
            label="Contraseña inicial"
            htmlFor="ca_pwd"
            hint="Mínimo 8 caracteres. El profesional la puede cambiar después en su perfil."
          >
            <div className="flex gap-2">
              <Input
                id="ca_pwd"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                className="font-mono"
              />
              <button
                type="button"
                onClick={() => setPassword(generatePassword())}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Generar
              </button>
            </div>
          </Field>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Roles</p>
            {roles.length === 0 ? (
              <p className="text-xs text-slate-500">Cargando roles…</p>
            ) : (
              <div className="space-y-1.5">
                {roles.map((r) => (
                  <label
                    key={r.id}
                    className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded p-1.5"
                  >
                    <input
                      type="checkbox"
                      checked={rolIds.includes(r.id)}
                      onChange={() =>
                        setRolIds(
                          rolIds.includes(r.id)
                            ? rolIds.filter((x) => x !== r.id)
                            : [...rolIds, r.id],
                        )
                      }
                      className="mt-0.5 rounded border-slate-300 text-[color:var(--color-link)]"
                    />
                    <div>
                      <div className="font-medium text-slate-900">{r.nombre}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.codigo}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Crear cuenta
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ---------- Edit dialog ----------

const TIPOS_COLEGIO_LABEL: Record<string, string> = {
  CMP: "CMP — Médico",
  COP: "COP — Odontólogo",
  COQ: "COQ — Químico Farmacéutico",
};

function EditProfesionalDialog({
  open,
  onClose,
  prof,
}: {
  open: boolean;
  onClose: () => void;
  prof: Profesional;
}) {
  const queryClient = useQueryClient();
  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  // `/policlinicos/{id}/sedes` es de super-admin: pedirlo como admin_tenant
  // devolvía 403 y react-query dejaba la lista vacía, así que el formulario
  // decía "no hay sedes" cuando sí las había. Para un usuario del tenant va
  // `/sedes`, que su rol sí puede leer.
  const {
    data: sedesCatalogo = [],
    isLoading: cargandoSedes,
    isError: errorSedes,
  } = useQuery({
    // Una sola clave: las dos ramas llamaban a `listSedes()` idéntico y
    // diferenciaban la caché por un `tenant_id` que el servidor ya no manda.
    queryKey: ["sedes"],
    queryFn: () =>
      listSedes(),
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const [form, setForm] = useState<ProfesionalUpdate>({});
  const [error, setError] = useState<string | null>(null);
  const [especialidadCodigos, setEspecialidadCodigos] = useState<string[]>([]);
  const [especialidadPrincipal, setEspecialidadPrincipal] = useState<string | null>(null);
  const [sedeIds, setSedeIds] = useState<number[]>([]);
  const [sedePrincipalId, setSedePrincipalId] = useState<number | null>(null);

  useEffect(() => {
    if (open && prof) {
      setForm({
        nombres: prof.nombres,
        ap_paterno: prof.ap_paterno,
        ap_materno: prof.ap_materno ?? "",
        email: prof.email ?? "",
        telefono: prof.telefono ?? "",
        rne: prof.rne ?? "",
        firma_digital_activa: prof.firma_digital_activa,
        activo: prof.activo,
      });
      setEspecialidadCodigos(prof.especialidades.map((e) => e.codigo));
      const principal = prof.especialidades.find((e) => e.principal);
      setEspecialidadPrincipal(principal?.codigo ?? null);
      setSedeIds(prof.sedes.map((s) => s.id));
      const sedePrincipal = prof.sedes.find((s) => s.principal);
      setSedePrincipalId(sedePrincipal?.id ?? null);
      setError(null);
    }
  }, [open, prof]);

  const mutation = useMutation({
    mutationFn: (payload: ProfesionalUpdate) => updateProfesional(prof.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profesional", prof.id] });
      queryClient.invalidateQueries({ queryKey: ["profesionales"] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "No se pudo guardar.");
      } else {
        setError("Error de conexión.");
      }
    },
  });

  function toggleEspecialidad(codigo: string) {
    if (especialidadCodigos.includes(codigo)) {
      const newList = especialidadCodigos.filter((c) => c !== codigo);
      setEspecialidadCodigos(newList);
      if (especialidadPrincipal === codigo) setEspecialidadPrincipal(null);
    } else {
      setEspecialidadCodigos([...especialidadCodigos, codigo]);
    }
  }

  function toggleSede(sedeId: number) {
    if (sedeIds.includes(sedeId)) {
      setSedeIds(sedeIds.filter((s) => s !== sedeId));
      if (sedePrincipalId === sedeId) setSedePrincipalId(null);
    } else {
      setSedeIds([...sedeIds, sedeId]);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...form,
      ap_materno: form.ap_materno || null,
      email: form.email || null,
      telefono: form.telefono || null,
      rne: form.rne || null,
      especialidad_codigos: especialidadCodigos,
      especialidad_principal: especialidadPrincipal,
      sede_ids: sedeIds,
      sede_principal_id: sedePrincipalId,
    });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Editar profesional" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Read-only: doc + colegiatura no se editan */}
        <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-0.5">
          <div>
            <span className="font-medium">Documento:</span>{" "}
            <span className="font-mono">
              {prof.tipo_documento} {prof.num_documento}
            </span>
          </div>
          <div>
            <span className="font-medium">Colegiatura:</span>{" "}
            <span className="font-mono">
              {TIPOS_COLEGIO_LABEL[prof.tipo_colegio] ?? prof.tipo_colegio} {prof.colegiatura}
            </span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">
            Documento y colegiatura no se editan. Si hay un error, da de baja al profesional y crea uno nuevo.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Nombres" htmlFor="e_nombres">
            <Input
              id="e_nombres"
              value={form.nombres ?? ""}
              onChange={(e) => setForm({ ...form, nombres: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. paterno" htmlFor="e_apat">
            <Input
              id="e_apat"
              value={form.ap_paterno ?? ""}
              onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. materno" htmlFor="e_amat">
            <Input
              id="e_amat"
              value={form.ap_materno ?? ""}
              onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono" htmlFor="e_tel">
            <TelefonoInput
              id="e_tel"
              value={form.telefono}
              onChange={(v) => setForm({ ...form, telefono: v })}
            />
          </Field>
          <Field label="Email" htmlFor="e_email">
            <Input
              id="e_email"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>

        <Field label="RNE" htmlFor="e_rne" hint="Registro Nacional de Especialistas. Solo para especialistas.">
          <Input
            id="e_rne"
            value={form.rne ?? ""}
            onChange={(e) => setForm({ ...form, rne: e.target.value })}
            className="font-mono"
          />
        </Field>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Especialidades</p>
          {especialidades.length === 0 ? (
            <p className="text-xs text-slate-500">Cargando catálogo…</p>
          ) : (
            <div className="grid grid-cols-2 gap-1 max-h-44 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {especialidades.map((e: EspecialidadMini) => {
                const checked = especialidadCodigos.includes(e.codigo);
                const isPrincipal = especialidadPrincipal === e.codigo;
                return (
                  <label
                    key={e.id}
                    className={`flex items-center gap-2 text-sm cursor-pointer rounded p-1.5 hover:bg-slate-50 ${checked ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEspecialidad(e.codigo)}
                      className="rounded border-slate-300 text-[color:var(--color-link)]"
                    />
                    <span className="flex-1 text-slate-700">{e.nombre}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={() =>
                          setEspecialidadPrincipal(isPrincipal ? null : e.codigo)
                        }
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          isPrincipal
                            ? "bg-[color:var(--color-link)] text-white"
                            : "border border-[color:var(--color-link)] text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                        }`}
                      >
                        {isPrincipal ? "Principal ✓" : "Principal"}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Sedes</p>
          {cargandoSedes ? (
            <p className="text-xs text-slate-500">Cargando sedes…</p>
          ) : errorSedes ? (
            <Alert>No se pudieron cargar las sedes. Recarga la página.</Alert>
          ) : sedesCatalogo.length === 0 ? (
            <p className="text-xs text-slate-500">
              No hay sedes registradas en este policlínico.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {sedesCatalogo.map((s) => {
                const checked = sedeIds.includes(s.id);
                const isPrincipal = sedePrincipalId === s.id;
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-2 text-sm cursor-pointer rounded p-1.5 hover:bg-slate-50 ${checked ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSede(s.id)}
                      className="rounded border-slate-300 text-[color:var(--color-link)]"
                    />
                    <span className="flex-1 text-slate-700">{s.nombre}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={() =>
                          setSedePrincipalId(isPrincipal ? null : s.id)
                        }
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          isPrincipal
                            ? "bg-[color:var(--color-link)] text-white"
                            : "border border-[color:var(--color-link)] text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                        }`}
                      >
                        {isPrincipal ? "Principal ✓" : "Principal"}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-2 pt-2 border-t border-slate-100">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.firma_digital_activa ?? false}
              onChange={(e) =>
                setForm({ ...form, firma_digital_activa: e.target.checked })
              }
              className="rounded border-slate-300 text-[color:var(--color-link)]"
            />
            Firma digital activa
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.activo ?? true}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })}
              className="rounded border-slate-300 text-[color:var(--color-link)]"
            />
            Profesional activo
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Agenda del profesional ----------

function AgendaProfesionalCard({
  prof,
  puedeEditar,
}: {
  prof: Profesional;
  puedeEditar: boolean;
}) {
  const queryClient = useQueryClient();
  const [reglaDialogOpen, setReglaDialogOpen] = useState(false);
  const [reglaEditando, setReglaEditando] = useState<AgendaRegla | null>(null);
  const [crearBloqueoOpen, setCrearBloqueoOpen] = useState(false);

  function abrirNueva() {
    setReglaEditando(null);
    setReglaDialogOpen(true);
  }
  function abrirEdicion(r: AgendaRegla) {
    setReglaEditando(r);
    setReglaDialogOpen(true);
  }

  const { data: reglas = [] } = useQuery({
    queryKey: ["agenda", "reglas", prof.id],
    queryFn: () => listReglas(prof.id),
  });
  const { data: bloqueos = [] } = useQuery({
    queryKey: ["agenda", "bloqueos", prof.id],
    queryFn: () =>
      listBloqueos(prof.id, new Date().toISOString().slice(0, 10)),
  });

  // Borrar un horario o un bloqueo cambia qué horas se ofrecen a los pacientes
  // a partir de ese momento: se confirma antes.
  const [reglaPorBorrar, setReglaPorBorrar] = useState<number | null>(null);
  const [bloqueoPorBorrar, setBloqueoPorBorrar] = useState<number | null>(null);

  const eliminarRegla = useMutation({
    mutationFn: (id: number) => deleteRegla(prof.id, id),
    onSuccess: () => {
      setReglaPorBorrar(null);
      queryClient.invalidateQueries({ queryKey: ["agenda", "reglas", prof.id] });
    },
  });
  const eliminarBloqueo = useMutation({
    mutationFn: (id: number) => deleteBloqueo(prof.id, id),
    onSuccess: () => {
      setBloqueoPorBorrar(null);
      queryClient.invalidateQueries({ queryKey: ["agenda", "bloqueos", prof.id] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[color:var(--color-link)]" />
              Agenda del profesional
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Disponibilidad semanal recurrente y bloqueos puntuales. Las citas
              se agendarán dentro de estos horarios.
            </p>
          </div>
          {puedeEditar && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setCrearBloqueoOpen(true)}>
                <CalendarOff className="w-4 h-4" />
                Registrar ausencia
              </Button>
              <Button onClick={abrirNueva}>
                <Plus className="w-4 h-4" />
                Nueva regla
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {/* Resumen semanal: qué días trabaja y en qué franja, de un vistazo.
            Es lo que alimenta el calendario del alta de citas. */}
        <div className="grid grid-cols-7 gap-px border-y border-slate-200 bg-slate-200">
          {DIAS_SEMANA.map((d, i) => {
            const delDia = reglas.filter((r) => r.dia_semana === i && r.activo);
            return (
              <div
                key={i}
                className={`px-2 py-2.5 text-center ${delDia.length ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : "bg-white"}`}
              >
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {d.slice(0, 3)}
                </div>
                {delDia.length === 0 ? (
                  <div className="mt-1 text-xs text-slate-300">—</div>
                ) : (
                  delDia.map((r) => (
                    <div key={r.id} className="mt-1 font-mono text-[11px] leading-tight text-[color:var(--color-link)]">
                      {r.hora_inicio.slice(0, 5)}
                      <br />
                      {r.hora_fin.slice(0, 5)}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>

        {reglas.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-500">
            Este profesional todavía no tiene horarios de atención, así que no se
            le puede agendar ninguna cita. Pulsa «Nuevo horario» y marca los días
            en que atiende.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <ThA>Día</ThA>
                  <ThA>Horario</ThA>
                  <ThA>Sede</ThA>
                  <ThA>Duración slot</ThA>
                  <ThA>Estado</ThA>
                  <ThA>&nbsp;</ThA>
                </tr>
              </thead>
              <tbody>
                {reglas.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="px-6 py-3 font-medium">
                      {DIAS_SEMANA[r.dia_semana]}
                    </td>
                    <td className="px-6 py-3 font-mono text-slate-700">
                      {r.hora_inicio.slice(0, 5)} – {r.hora_fin.slice(0, 5)}
                    </td>
                    <td className="px-6 py-3 text-slate-700">{r.sede_nombre}</td>
                    <td className="px-6 py-3 text-xs text-slate-600">
                      {r.duracion_min} min
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={r.activo ? "success" : "danger"}>
                        {r.activo ? "Activa" : "Inactiva"}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {puedeEditar && (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => abrirEdicion(r)}
                            className="text-xs text-slate-500 hover:text-[color:var(--color-link)]"
                            title="Editar horario"
                            aria-label={`Editar horario del ${DIAS_SEMANA[r.dia_semana]}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 inline" />
                          </button>
                          <button
                            onClick={() => setReglaPorBorrar(r.id)}
                            disabled={eliminarRegla.isPending}
                            className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-50"
                            title="Eliminar horario"
                            aria-label={`Eliminar horario del ${DIAS_SEMANA[r.dia_semana]}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 inline" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {bloqueos.length > 0 && (
          <div className="border-t border-slate-200">
            <div className="px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50">
              Ausencias próximas
            </div>
            <table className="w-full text-sm">
              <tbody>
                {bloqueos.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="whitespace-nowrap px-6 py-2 font-mono text-xs text-slate-700">
                      {b.fecha_inicio === b.fecha_fin
                        ? b.fecha_inicio
                        : `${b.fecha_inicio} → ${b.fecha_fin}`}
                    </td>
                    <td className="whitespace-nowrap px-6 py-2 text-xs">
                      {/* Día completo vs franja: sin esto no se distingue una
                          licencia de "esa mañana no puedo". */}
                      {b.hora_inicio && b.hora_fin ? (
                        <Badge variant="warning">
                          {b.hora_inicio.slice(0, 5)} – {b.hora_fin.slice(0, 5)}
                        </Badge>
                      ) : (
                        <Badge variant="danger">Día completo</Badge>
                      )}
                    </td>
                    <td className="px-6 py-2 text-xs text-slate-600">
                      {b.motivo ?? "—"}
                    </td>
                    <td className="px-6 py-2 text-right">
                      {puedeEditar && (
                        <button
                          onClick={() => setBloqueoPorBorrar(b.id)}
                          disabled={eliminarBloqueo.isPending}
                          className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      <ReglaDialog
        prof={prof}
        open={reglaDialogOpen}
        editando={reglaEditando}
        onClose={() => setReglaDialogOpen(false)}
      />
      <CrearBloqueoDialog
        prof={prof}
        reglas={reglas}
        bloqueos={bloqueos}
        open={crearBloqueoOpen}
        onClose={() => setCrearBloqueoOpen(false)}
      />

      <ConfirmDialog
        open={reglaPorBorrar !== null}
        onClose={() => setReglaPorBorrar(null)}
        onConfirm={() => reglaPorBorrar != null && eliminarRegla.mutate(reglaPorBorrar)}
        loading={eliminarRegla.isPending}
        title="Borrar horario de atención"
        description="Ese día dejará de ofrecerse a los pacientes. Las citas ya agendadas en esa franja no se tocan."
        confirmLabel="Sí, borrar el horario"
        cancelLabel="No, dejarlo"
      />

      <ConfirmDialog
        open={bloqueoPorBorrar !== null}
        onClose={() => setBloqueoPorBorrar(null)}
        onConfirm={() =>
          bloqueoPorBorrar != null && eliminarBloqueo.mutate(bloqueoPorBorrar)
        }
        loading={eliminarBloqueo.isPending}
        title="Quitar bloqueo"
        description="Esas fechas vuelven a estar disponibles y se podrán agendar citas en ellas."
        confirmLabel="Sí, quitar el bloqueo"
        cancelLabel="No, dejarlo"
      />
    </Card>
  );
}

function ThA({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

/**
 * Alta y edición de reglas de agenda.
 *
 * Al crear acepta VARIOS días a la vez: un "lunes a viernes de 08:00 a 13:00"
 * es una sola operación y no cinco pasadas por el mismo formulario, que era lo
 * que hacía sentir esta pantalla como un trámite en vez de programar un horario.
 * Al editar trabaja sobre un único día, que es la regla que se está tocando.
 */
function ReglaDialog({
  prof,
  open,
  onClose,
  editando,
}: {
  prof: Profesional;
  open: boolean;
  onClose: () => void;
  /** Regla existente → modo edición. null → alta. */
  editando: AgendaRegla | null;
}) {
  const queryClient = useQueryClient();
  const esEdicion = editando != null;

  const [dias, setDias] = useState<number[]>([0, 1, 2, 3, 4]);
  const [sedeId, setSedeId] = useState<number>(prof.sedes[0]?.id ?? 0);
  const [horaInicio, setHoraInicio] = useState("08:00");
  const [horaFin, setHoraFin] = useState("13:00");
  const [duracion, setDuracion] = useState(30);
  const [activo, setActivo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editando) {
      setDias([editando.dia_semana]);
      setSedeId(editando.sede_id);
      setHoraInicio(editando.hora_inicio.slice(0, 5));
      setHoraFin(editando.hora_fin.slice(0, 5));
      setDuracion(editando.duracion_min);
      setActivo(editando.activo);
    } else {
      setDias([0, 1, 2, 3, 4]);
      setSedeId(prof.sedes[0]?.id ?? 0);
      setHoraInicio("08:00");
      setHoraFin("13:00");
      setDuracion(30);
      setActivo(true);
    }
  }, [open, editando, prof.sedes]);

  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ["agenda", "reglas", prof.id] });
    onClose();
  };

  const fallo = (err: unknown, porDefecto: string) => {
    const detail =
      err instanceof ApiError ? (err.payload as { detail?: string })?.detail : null;
    setError(detail ?? porDefecto);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const hhmmss = (v: string) => (v.length === 5 ? `${v}:00` : v);
      const base = {
        sede_id: sedeId,
        hora_inicio: hhmmss(horaInicio),
        hora_fin: hhmmss(horaFin),
        duracion_min: duracion,
        activo,
      };
      if (editando) {
        await updateRegla(prof.id, editando.id, { ...base, dia_semana: dias[0] });
        return;
      }
      // Un día = una regla. Secuencial y no en paralelo: si una falla, el
      // mensaje del backend llega limpio en vez de mezclado con las demás.
      for (const dia of dias) {
        await createRegla(prof.id, { ...base, dia_semana: dia });
      }
    },
    onSuccess: refrescar,
    onError: (e) => fallo(e, "No se pudo guardar el horario."),
  });

  function alternarDia(d: number) {
    setDias((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort(),
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sedeId) return setError("Selecciona la sede.");
    if (dias.length === 0) return setError("Marca al menos un día.");
    if (horaInicio >= horaFin) return setError("La hora de inicio debe ser anterior a la de fin.");
    guardar.mutate();
  }

  // Cuántas citas entran por día, para que el número de minutos deje de ser
  // abstracto al configurarlo.
  const cupos = (() => {
    const [hi, mi] = horaInicio.split(":").map(Number);
    const [hf, mf] = horaFin.split(":").map(Number);
    const mins = hf * 60 + mf - (hi * 60 + mi);
    return mins > 0 && duracion > 0 ? Math.floor(mins / duracion) : 0;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={esEdicion ? "Editar horario" : "Nuevo horario de atención"}
      description={
        esEdicion
          ? "Cambia la franja de este día."
          : "Marca los días y define la franja. Recepción solo podrá agendar dentro de ella."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {prof.sedes.length === 0 && (
          <Alert>
            El profesional no tiene sedes asignadas. Asígnale al menos una antes
            de configurar su agenda.
          </Alert>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">
            {esEdicion ? "Día" : "Días de atención"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {DIAS_SEMANA.map((d, i) => {
              const marcado = dias.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => (esEdicion ? setDias([i]) : alternarDia(i))}
                  aria-pressed={marcado}
                  className={`rounded-lg border px-3 py-1.5 text-sm transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                    marcado
                      ? "border-[color:var(--color-link)] bg-[color:var(--color-link)] font-medium text-white"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {d.slice(0, 3)}
                </button>
              );
            })}
          </div>
          {!esEdicion && (
            <p className="mt-1.5 text-xs text-slate-500">
              Se creará una franja por cada día marcado ({dias.length}).
            </p>
          )}
        </div>

        <Field label="Sede" htmlFor="r_sede">
          <select
            id="r_sede"
            value={sedeId}
            onChange={(e) => setSedeId(Number(e.target.value))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="">— seleccionar —</option>
            {prof.sedes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Desde" htmlFor="r_hi">
            <Input
              id="r_hi"
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
              required
            />
          </Field>
          <Field label="Hasta" htmlFor="r_hf">
            <Input
              id="r_hf"
              type="time"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
              required
            />
          </Field>
          <Field label="Minutos por cita" htmlFor="r_dur">
            <Input
              id="r_dur"
              type="number"
              value={duracion}
              onChange={(e) => setDuracion(Number(e.target.value))}
              min={5}
              max={240}
              required
            />
          </Field>
        </div>

        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {cupos > 0 ? (
            <>
              Entran <strong>{cupos} citas</strong> por día de {duracion} min, de{" "}
              {horaInicio} a {horaFin}
              {!esEdicion && dias.length > 1 && <> · {cupos * dias.length} a la semana</>}.
            </>
          ) : (
            "Revisa las horas: la franja no da para ninguna cita."
          )}
        </p>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="rounded border-slate-300 text-[color:var(--color-link)]"
          />
          Activo — desmárcalo para suspender el horario sin borrarlo
        </label>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={guardar.isPending}
            disabled={prof.sedes.length === 0}
          >
            {esEdicion ? "Guardar cambios" : "Crear horario"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Ausencias puntuales: un día suelto, un rango de días, o solo unas horas.
 *
 * El caso frecuente no es "me voy de vacaciones", es "este viernes no puedo" o
 * "el lunes por la mañana tengo un trámite". Antes solo se podía tumbar el día
 * entero, así que para media jornada había que borrar la regla semanal y
 * rehacerla: destruir la configuración permanente para resolver una semana.
 *
 * Los presets cubren lo que se pide de verdad; "otras horas" queda para el
 * resto. La regla semanal no se toca nunca: la semana siguiente vuelve sola.
 */
function CrearBloqueoDialog({
  prof,
  open,
  onClose,
  reglas,
  bloqueos,
}: {
  prof: Profesional;
  open: boolean;
  onClose: () => void;
  reglas: AgendaRegla[];
  bloqueos: AgendaBloqueo[];
}) {
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();
  type Alcance = "dia" | "manana" | "tarde" | "horas" | "rango";
  const [alcance, setAlcance] = useState<Alcance>("dia");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [horaIni, setHoraIni] = useState("08:00");
  const [horaFin, setHoraFin] = useState("13:00");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const hoyLocal = hoyEnZona(tz);

  useEffect(() => {
    if (!open) return;
    // Arranca en el próximo día que atiende: abrir en un día suelto donde no
    // trabaja obligaría a navegar antes de poder hacer nada.
    const inicial = proximoDiaDisponible(hoyLocal, { reglas, bloqueos }) ?? hoyLocal;
    setAlcance("dia");
    setDesde(inicial);
    setHasta(inicial);
    setHoraIni("08:00");
    setHoraFin("13:00");
    setMotivo("");
    setError(null);
  }, [open, reglas, bloqueos, hoyLocal]);

  // Franja que atiende el día elegido: sirve para proponer horas con sentido
  // y para avisar si el bloqueo no va a tener ningún efecto.
  const reglasDelDia = (() => {
    if (!desde) return [];
    const dow = (new Date(`${desde}T12:00:00`).getDay() + 6) % 7;
    return reglas.filter((r) => r.activo && r.dia_semana === dow);
  })();
  const atiendeEseDia = reglasDelDia.length > 0;
  const franja = atiendeEseDia
    ? {
        ini: reglasDelDia.map((r) => r.hora_inicio.slice(0, 5)).sort()[0],
        fin: reglasDelDia.map((r) => r.hora_fin.slice(0, 5)).sort().at(-1) as string,
      }
    : null;

  // Mediodía del profesional, para partir mañana/tarde donde le corresponde y
  // no en un 12:00 inventado.
  const corte = (() => {
    if (!franja) return "13:00";
    const [hi] = franja.ini.split(":").map(Number);
    const [hf] = franja.fin.split(":").map(Number);
    const medio = Math.round((hi + hf) / 2);
    return `${String(medio).padStart(2, "0")}:00`;
  })();

  // Sin horarios configurados no hay ausencia posible: no hay nada que anular.
  const sinHorarios = reglas.filter((r) => r.activo).length === 0;

  /**
   * Al cambiar de alcance, corrige la fecha si dejó de ser válida.
   * En "varios días" se elige libre; al volver a un alcance de un solo día esa
   * fecha puede caer en un día que no atiende, y el calendario mostraría
   * seleccionado un día deshabilitado.
   */
  function cambiarAlcance(nuevo: Alcance) {
    setAlcance(nuevo);
    if (nuevo === "rango") return;
    const dow = (new Date(`${desde}T12:00:00`).getDay() + 6) % 7;
    const atiende = reglas.some((r) => r.activo && r.dia_semana === dow);
    if (!atiende || desde < hoyLocal) {
      const ok = proximoDiaDisponible(
        desde < hoyLocal ? hoyLocal : desde,
        { reglas, bloqueos },
      );
      if (ok) {
        setDesde(ok);
        setHasta(ok);
      }
    } else {
      setHasta(desde);
    }
  }

  const mutation = useMutation({
    mutationFn: (p: AgendaBloqueoCreate) => createBloqueo(prof.id, p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agenda", "bloqueos", prof.id] });
      onClose();
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError ? (err.payload as { detail?: string })?.detail : null;
      setError(
        typeof detail === "string" ? detail : "No se pudo registrar la ausencia.",
      );
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    let hi: string | null = null;
    let hf: string | null = null;
    if (alcance === "manana") {
      hi = franja?.ini ?? "00:00";
      hf = corte;
    } else if (alcance === "tarde") {
      hi = corte;
      hf = franja?.fin ?? "23:59";
    } else if (alcance === "horas") {
      hi = horaIni;
      hf = horaFin;
    }
    if (hi && hf && hi >= hf) {
      setError("La hora de inicio debe ser anterior a la de fin.");
      return;
    }

    mutation.mutate({
      fecha_inicio: desde,
      fecha_fin: alcance === "rango" ? hasta : desde,
      hora_inicio: hi ? `${hi}:00` : null,
      hora_fin: hf ? `${hf}:00` : null,
      motivo: motivo.trim() || null,
    });
  }

  const OPCIONES: { id: Alcance; label: string; detalle: string }[] = [
    { id: "dia", label: "Todo el día", detalle: "No atiende esa fecha" },
    {
      id: "manana",
      label: "Solo la mañana",
      detalle: franja ? `${franja.ini} – ${corte}` : "—",
    },
    {
      id: "tarde",
      label: "Solo la tarde",
      detalle: franja ? `${corte} – ${franja.fin}` : "—",
    },
    { id: "horas", label: "Otras horas", detalle: "Tú eliges la franja" },
    { id: "rango", label: "Varios días", detalle: "Vacaciones, licencia" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Registrar ausencia"
      description="Se aplica solo a estas fechas. El horario semanal del profesional no cambia."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {sinHorarios && (
          <Alert>
            Este profesional no tiene horarios de atención, así que no hay nada
            que anular. Configúrale primero su horario semanal.
          </Alert>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">¿Cuánto?</p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {OPCIONES.map((o) => {
              const activa = alcance === o.id;
              const inutil = (o.id === "manana" || o.id === "tarde") && !franja;
              return (
                <button
                  key={o.id}
                  type="button"
                  disabled={inutil}
                  onClick={() => cambiarAlcance(o.id)}
                  aria-pressed={activa}
                  className={`rounded-lg border px-3 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:opacity-40 ${
                    activa
                      ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="block text-sm font-medium text-slate-800">
                    {o.label}
                  </span>
                  <span className="block font-mono text-xs text-slate-500">
                    {o.detalle}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* La fecha va DESPUÉS del alcance porque el alcance decide qué se
            puede elegir: para una ausencia de un día solo tienen sentido los
            días que el profesional atiende — en los demás no hay nada que
            ausentar. Para vacaciones el rango es libre: que incluya domingos es
            normal y no molesta. */}
        {alcance !== "rango" ? (
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">¿Qué día?</p>
            <CalendarioDisponibilidad
              value={desde}
              disponibilidad={{ reglas, bloqueos, hoy: hoyLocal }}
              onChange={(f) => {
                setDesde(f);
                setHasta(f);
              }}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Desde el día" htmlFor="b_d">
              <Input
                id="b_d"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                required
              />
            </Field>
            <Field label="Hasta el día" htmlFor="b_h" hint="Ambos incluidos.">
              <Input
                id="b_h"
                type="date"
                value={hasta}
                min={desde}
                onChange={(e) => setHasta(e.target.value)}
                required
              />
            </Field>
          </div>
        )}

        {alcance === "horas" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Desde las" htmlFor="b_hi">
              <Input
                id="b_hi"
                type="time"
                value={horaIni}
                onChange={(e) => setHoraIni(e.target.value)}
                required
              />
            </Field>
            <Field label="Hasta las" htmlFor="b_hf">
              <Input
                id="b_hf"
                type="time"
                value={horaFin}
                onChange={(e) => setHoraFin(e.target.value)}
                required
              />
            </Field>
          </div>
        )}

        <Field
          label="Motivo (opcional)"
          htmlFor="b_m"
          hint="Queda registrado. Ayuda a recepción a explicar la reprogramación."
        >
          <Input
            id="b_m"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. emergencia familiar, capacitación, feriado"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending} disabled={sinHorarios}>
            Registrar ausencia
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
